import { useState, useCallback, useEffect } from 'react';
import { socket } from '../lib/socket';
import { useSocketEvent } from './useSocketEvent';
import {
  EVENTS,
  type GameState,
  type GameMode,
  type GameVariant,
  type TableTier,
  type Card,
  type JoinSuccessPayload,
  type SpectateSuccessPayload,
  type ErrorPayload,
  type ChallengeCompletePayload,
  type DailyChallengeCompletePayload,
} from '@flip7/shared';

const SESSION_KEY_PREFIX = 'flip7_session_';
const LAST_ROOM_KEY = 'flip7_lastRoom';

interface SessionData {
  roomCode: string;
  playerId?: string;
  spectatorId?: string;
  sessionToken: string;
}

function saveSession(data: SessionData) {
  try {
    localStorage.setItem(`${SESSION_KEY_PREFIX}${data.roomCode}`, JSON.stringify(data));
    localStorage.setItem(LAST_ROOM_KEY, data.roomCode);
  } catch {}
}

function loadSession(roomCode?: string): SessionData | null {
  try {
    const code = roomCode ?? localStorage.getItem(LAST_ROOM_KEY);
    if (!code) return null;
    const raw = localStorage.getItem(`${SESSION_KEY_PREFIX}${code}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearSession(roomCode?: string) {
  try {
    const code = roomCode ?? localStorage.getItem(LAST_ROOM_KEY);
    if (code) localStorage.removeItem(`${SESSION_KEY_PREFIX}${code}`);
    localStorage.removeItem(LAST_ROOM_KEY);
  } catch {}
}

export function useGameRoom() {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [spectatorId, setSpectatorId] = useState<string | null>(null);
  const [sessionReplaced, setSessionReplaced] = useState(false);
  const [challengeResult, setChallengeResult] = useState<ChallengeCompletePayload | null>(null);
  const [dailyResult, setDailyResult] = useState<DailyChallengeCompletePayload | null>(null);

  useSocketEvent<GameState>(EVENTS.GAME_STATE, (state) => {
    setGameState(state);
    setError(null);
  });

  useSocketEvent<JoinSuccessPayload>(EVENTS.JOIN_SUCCESS, (data) => {
    setRoomCode(data.gameState.roomCode);
    setGameState(data.gameState);
    setPlayerId(data.playerId);
    setSpectatorId(null);
    setError(null);
    setSessionReplaced(false);
    saveSession({
      roomCode: data.gameState.roomCode,
      playerId: data.playerId,
      sessionToken: data.sessionToken,
    });
  });

  useSocketEvent<SpectateSuccessPayload>(EVENTS.SPECTATE_SUCCESS, (data) => {
    setRoomCode(data.gameState.roomCode);
    setGameState(data.gameState);
    setSpectatorId(data.spectatorId);
    setPlayerId(null);
    setError(null);
    setSessionReplaced(false);
    saveSession({
      roomCode: data.gameState.roomCode,
      spectatorId: data.spectatorId,
      sessionToken: data.sessionToken,
    });
  });

  useSocketEvent<ErrorPayload>(EVENTS.ERROR, (data) => {
    setError(data.message);
    // If reconnect failed, clear stale session and go to home
    if (data.code === 'RECONNECT_FAILED') {
      clearSession();
      setGameState(null);
      setRoomCode(null);
      setPlayerId(null);
      setSpectatorId(null);
    }
  });

  useSocketEvent(EVENTS.SESSION_REPLACED, () => {
    setSessionReplaced(true);
  });

  useSocketEvent<ChallengeCompletePayload>(EVENTS.CHALLENGE_COMPLETE, (data) => {
    setChallengeResult(data);
  });

  useSocketEvent<DailyChallengeCompletePayload>(EVENTS.DAILY_CHALLENGE_COMPLETE, (data) => {
    setDailyResult(data);
  });

  // Auto-reconnect on mount if we have a stored session
  useEffect(() => {
    const session = loadSession();
    if (session) {
      socket.connect();
      socket.emit(EVENTS.RECONNECT, { sessionToken: session.sessionToken });
    }
  }, []);

  // Re-register after socket reconnect (e.g., tab backgrounding)
  useEffect(() => {
    function onConnect() {
      const session = loadSession();
      if (session && !sessionReplaced) {
        socket.emit(EVENTS.RECONNECT, { sessionToken: session.sessionToken });
      }
    }
    socket.on('connect', onConnect);
    return () => { socket.off('connect', onConnect); };
  }, [sessionReplaced]);

  const createRoom = useCallback((playerName: string, targetScore: number, mode?: GameMode, botCount?: number, tier?: TableTier, variant?: GameVariant) => {
    socket.connect();
    socket.emit(EVENTS.CREATE_ROOM, { playerName, targetScore, mode, botCount, tier, variant });
  }, []);

  const joinRoom = useCallback((code: string, playerName: string) => {
    socket.connect();
    setRoomCode(code);
    socket.emit(EVENTS.JOIN_ROOM, { roomCode: code, playerName });
  }, []);

  const spectateRoom = useCallback((code: string, playerName: string, autoQueue = false) => {
    socket.connect();
    setRoomCode(code);
    socket.emit(EVENTS.SPECTATE_ROOM, { roomCode: code, playerName, autoQueue });
  }, []);

  const joinGroup = useCallback((groupSlug: string, playerName: string) => {
    socket.connect();
    socket.emit(EVENTS.JOIN_GROUP, { groupSlug, playerName });
  }, []);

  const toggleWantToPlay = useCallback(() => {
    if (roomCode) socket.emit(EVENTS.TOGGLE_WANT_TO_PLAY, { roomCode });
  }, [roomCode]);

  const kickSpectator = useCallback((targetSpectatorId: string) => {
    if (roomCode) socket.emit(EVENTS.KICK_SPECTATOR, { roomCode, spectatorId: targetSpectatorId });
  }, [roomCode]);

  const reconnect = useCallback((roomCodeOverride?: string) => {
    const session = loadSession(roomCodeOverride);
    if (session) {
      socket.connect();
      socket.emit(EVENTS.RECONNECT, { sessionToken: session.sessionToken });
    }
  }, []);

  const startGame = useCallback(() => {
    if (!roomCode) return;
    // Hot Streak uses its own start event
    if (gameState?.mode === 'hot-streak') {
      socket.emit(EVENTS.HOT_STREAK_START_GAME, { roomCode });
    } else {
      socket.emit(EVENTS.START_GAME, { roomCode });
    }
  }, [roomCode, gameState?.mode]);

  const addCard = useCallback((targetPlayerId: string, card: Card) => {
    if (roomCode) socket.emit(EVENTS.ADD_CARD, { roomCode, playerId: targetPlayerId, card });
  }, [roomCode]);

  const removeCard = useCallback((targetPlayerId: string, card: Card) => {
    if (roomCode) socket.emit(EVENTS.REMOVE_CARD, { roomCode, playerId: targetPlayerId, card });
  }, [roomCode]);

  const stay = useCallback((targetPlayerId: string) => {
    if (roomCode) socket.emit(EVENTS.STAY, { roomCode, playerId: targetPlayerId });
  }, [roomCode]);

  const bust = useCallback((targetPlayerId: string) => {
    if (roomCode) socket.emit(EVENTS.BUST, { roomCode, playerId: targetPlayerId });
  }, [roomCode]);

  const nextPlayer = useCallback(() => {
    if (roomCode) socket.emit(EVENTS.NEXT_PLAYER, { roomCode });
  }, [roomCode]);

  const undo = useCallback(() => {
    if (roomCode) socket.emit(EVENTS.UNDO, { roomCode });
  }, [roomCode]);

  const endRound = useCallback(() => {
    if (roomCode) socket.emit(EVENTS.END_ROUND, { roomCode });
  }, [roomCode]);

  const nextRound = useCallback(() => {
    if (roomCode) socket.emit(EVENTS.NEXT_ROUND, { roomCode });
  }, [roomCode]);

  const reorderPlayers = useCallback((playerIds: string[]) => {
    if (roomCode) socket.emit(EVENTS.REORDER_PLAYERS, { roomCode, playerIds });
  }, [roomCode]);

  const addPlayer = useCallback((playerName: string) => {
    if (roomCode) socket.emit(EVENTS.ADD_PLAYER, { roomCode, playerName });
  }, [roomCode]);

  const removePlayer = useCallback((targetPlayerId: string) => {
    if (roomCode) socket.emit(EVENTS.REMOVE_PLAYER, { roomCode, playerId: targetPlayerId });
  }, [roomCode]);

  const hit = useCallback((targetPlayerId: string) => {
    if (roomCode) socket.emit(EVENTS.HIT, { roomCode, playerId: targetPlayerId });
  }, [roomCode]);

  const chooseTarget = useCallback((targetPlayerId: string) => {
    if (roomCode && playerId) socket.emit(EVENTS.CHOOSE_TARGET, { roomCode, playerId, targetPlayerId });
  }, [roomCode, playerId]);

  const startChallenge = useCallback((chapter: number, level: number) => {
    socket.connect();
    setChallengeResult(null);
    socket.emit(EVENTS.START_CHALLENGE, { chapter, level });
  }, []);

  const startDailyChallenge = useCallback((challengeId: string) => {
    socket.connect();
    setDailyResult(null);
    socket.emit(EVENTS.START_DAILY_CHALLENGE, { challengeId });
  }, []);

  const selectSwapCards = useCallback((sourcePlayerId: string, sourceCardIndex: number, targetPlayerId: string, targetCardIndex: number) => {
    if (roomCode) socket.emit(EVENTS.SELECT_SWAP_CARDS, { roomCode, sourcePlayerId, sourceCardIndex, targetPlayerId, targetCardIndex });
  }, [roomCode]);

  const selectStealTarget = useCallback((targetPlayerId: string, cardIndex: number) => {
    if (roomCode) socket.emit(EVENTS.SELECT_STEAL_TARGET, { roomCode, targetPlayerId, cardIndex });
  }, [roomCode]);

  const selectDiscardTarget = useCallback((targetPlayerId: string, cardIndex: number) => {
    if (roomCode) socket.emit(EVENTS.SELECT_DISCARD_TARGET, { roomCode, targetPlayerId, cardIndex });
  }, [roomCode]);

  const forfeitAction = useCallback(() => {
    if (roomCode) socket.emit(EVENTS.FORFEIT_ACTION, { roomCode });
  }, [roomCode]);

  const playAgain = useCallback(() => {
    if (roomCode) socket.emit(EVENTS.PLAY_AGAIN, { roomCode });
  }, [roomCode]);

  const leaveRoom = useCallback(() => {
    if (roomCode) {
      socket.emit(EVENTS.LEAVE_ROOM, { roomCode });
      clearSession(roomCode);
      setGameState(null);
      setRoomCode(null);
      setPlayerId(null);
      setSpectatorId(null);
      setError(null);
      window.history.replaceState({ screen: 'home' }, '', '/');
    }
  }, [roomCode]);

  return {
    gameState,
    roomCode,
    playerId,
    spectatorId,
    error,
    sessionReplaced,
    createRoom,
    joinRoom,
    spectateRoom,
    joinGroup,
    reconnect,
    startGame,
    addCard,
    removeCard,
    stay,
    bust,
    nextPlayer,
    undo,
    endRound,
    nextRound,
    reorderPlayers,
    addPlayer,
    removePlayer,
    hit,
    chooseTarget,
    playAgain,
    leaveRoom,
    toggleWantToPlay,
    kickSpectator,
    selectSwapCards,
    selectStealTarget,
    selectDiscardTarget,
    forfeitAction,
    startChallenge,
    challengeResult,
    startDailyChallenge,
    dailyResult,
  };
}
